import TiptapImage from '@tiptap/extension-image'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'

const MIN_IMAGE_SIZE = 50
const RESIZE_DIRECTIONS = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
] as const

type ResizeDirection = (typeof RESIZE_DIRECTIONS)[number]
type ImageDimensions = { width: number; height: number }

function normalizeDimension(value: unknown): number | null {
  const dimension = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(dimension) && dimension > 0
    ? Math.round(dimension)
    : null
}

function createResizeHandle(direction: ResizeDirection) {
  const handle = document.createElement('div')
  handle.dataset.resizeHandle = direction
  handle.setAttribute('role', 'presentation')
  return handle
}

export const ResizableImage = TiptapImage.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element) =>
          normalizeDimension(element.getAttribute('width')),
        renderHTML: (attributes) => {
          const width = normalizeDimension(attributes.width)
          return width ? { width } : {}
        },
      },
      height: {
        default: null,
        parseHTML: (element) =>
          normalizeDimension(element.getAttribute('height')),
        renderHTML: (attributes) => {
          const height = normalizeDimension(attributes.height)
          return height ? { height } : {}
        },
      },
    }
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      let currentNode = node
      let activeDirection: ResizeDirection | null = null
      let startPointer = { x: 0, y: 0 }
      let startDimensions: ImageDimensions = { width: 0, height: 0 }
      let pendingDimensions: ImageDimensions = { width: 0, height: 0 }

      const container = document.createElement('div')
      container.dataset.resizeContainer = ''
      container.dataset.dragHandle = ''
      container.draggable = true

      const wrapper = document.createElement('div')
      wrapper.dataset.resizeWrapper = ''

      const image = document.createElement('img')
      image.draggable = false
      wrapper.appendChild(image)
      container.appendChild(wrapper)

      const handles = RESIZE_DIRECTIONS.map((direction) => {
        const handle = createResizeHandle(direction)
        wrapper.appendChild(handle)
        return handle
      })

      const applyDimensions = ({ width, height }: ImageDimensions) => {
        image.style.width = `${Math.round(width)}px`
        image.style.height = `${Math.round(height)}px`
      }

      const syncImageAttributes = (updatedNode: ProseMirrorNode) => {
        const { src, alt, title } = updatedNode.attrs
        image.src = typeof src === 'string' ? src : ''
        image.alt = typeof alt === 'string' ? alt : ''
        image.title = typeof title === 'string' ? title : ''

        const width = normalizeDimension(updatedNode.attrs.width)
        const height = normalizeDimension(updatedNode.attrs.height)
        if (width && height) {
          applyDimensions({ width, height })
        } else {
          image.style.removeProperty('width')
          image.style.removeProperty('height')
        }
      }

      const syncEditableState = () => {
        handles.forEach((handle) => {
          handle.hidden = !editor.isEditable
        })
      }

      const finishResize = () => {
        if (!activeDirection) return
        activeDirection = null
        container.dataset.resizeState = 'false'

        const pos = getPos()
        if (typeof pos === 'number') {
          const imageNode = editor.state.doc.nodeAt(pos)
          if (imageNode?.type.name === this.name) {
            editor.view.dispatch(
              editor.state.tr.setNodeMarkup(pos, undefined, {
                ...imageNode.attrs,
                width: Math.round(pendingDimensions.width),
                height: Math.round(pendingDimensions.height),
              })
            )
          }
        }

        document.removeEventListener('mousemove', handlePointerMove)
        document.removeEventListener('mouseup', finishResize)
      }

      const handlePointerMove = (event: MouseEvent) => {
        if (!activeDirection) return
        event.preventDefault()

        const deltaX = event.clientX - startPointer.x
        const horizontalDelta = activeDirection.includes('left')
          ? -deltaX
          : deltaX
        const editorWidth = editor.view.dom.getBoundingClientRect().width
        const maxWidth = Math.max(MIN_IMAGE_SIZE, editorWidth)
        const width = Math.min(
          maxWidth,
          Math.max(MIN_IMAGE_SIZE, startDimensions.width + horizontalDelta)
        )
        const aspectRatio = startDimensions.width / startDimensions.height
        const height = width / (aspectRatio || 1)

        pendingDimensions = { width, height }
        applyDimensions(pendingDimensions)
      }

      const startResize = (event: MouseEvent, direction: ResizeDirection) => {
        event.preventDefault()
        event.stopPropagation()

        const rect = image.getBoundingClientRect()
        const storedWidth = normalizeDimension(currentNode.attrs.width)
        const storedHeight = normalizeDimension(currentNode.attrs.height)
        const width = rect.width || storedWidth || image.naturalWidth
        const height = rect.height || storedHeight || image.naturalHeight
        if (!width || !height) return

        activeDirection = direction
        startPointer = { x: event.clientX, y: event.clientY }
        startDimensions = { width, height }
        pendingDimensions = startDimensions
        container.dataset.resizeState = 'true'

        document.addEventListener('mousemove', handlePointerMove)
        document.addEventListener('mouseup', finishResize)
      }

      handles.forEach((handle) => {
        const direction = handle.dataset.resizeHandle as ResizeDirection
        handle.addEventListener('mousedown', (event) =>
          startResize(event, direction)
        )
      })

      syncImageAttributes(currentNode)
      syncEditableState()
      editor.on('update', syncEditableState)

      return {
        dom: container,
        update(updatedNode) {
          if (updatedNode.type !== currentNode.type) return false
          currentNode = updatedNode
          syncImageAttributes(updatedNode)
          syncEditableState()
          return true
        },
        selectNode() {
          container.dataset.selected = 'true'
        },
        deselectNode() {
          container.dataset.selected = 'false'
        },
        stopEvent(event) {
          const target = event.target
          return (
            target instanceof Element &&
            Boolean(target.closest('[data-resize-handle]'))
          )
        },
        destroy() {
          editor.off('update', syncEditableState)
          document.removeEventListener('mousemove', handlePointerMove)
          document.removeEventListener('mouseup', finishResize)
        },
      }
    }
  },
})
